import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, map } from 'rxjs/operators';
import { R } from '@core/utils/r';
import { CompanyHolidayAPI } from '@business/api/company_holiday';
import { CompanyHolidayId, CompanyHolidayRequest, CompanyHolidayResponse } from '@business/entities/company_holiday';

export class CompanyHolidayBloc extends BaseBloc {
  private readonly holidayRepo = new Repository<CompanyHolidayResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.holidayRepo.items$.pipe(map(items => R.sortBy(items, item => item.startDate)));

  readonly selectItem = this.holidayRepo.selectItem;

  onReset() {
    this.holidayRepo.reset();
  }

  fetchCompanyHolidays = () => {
    return CompanyHolidayAPI.fetchCompanyHolidays().pipe(
      this.holidayRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_company_holidays_error') }),
    );
  };

  createCompanyHoliday = (request: CompanyHolidayRequest) => {
    return CompanyHolidayAPI.createCompanyHoliday(request).pipe(
      this.holidayRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_company_holiday'),
      }),
    );
  };

  updateCompanyHoliday = (id: CompanyHolidayId, request: CompanyHolidayRequest) => {
    return CompanyHolidayAPI.updateCompanyHoliday(id, request).pipe(
      this.holidayRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_company_holiday'),
      }),
    );
  };

  deleteCompanyHoliday = (id: CompanyHolidayId) => {
    return showDeleteConfirmation('Delete Holiday', 'Remove this holiday? Future schedule imports and previews will treat the dates as working days again.').pipe(
      switchMap(() => CompanyHolidayAPI.deleteCompanyHoliday(id)),
      this.holidayRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_company_holiday'),
      }),
    );
  };
}
